import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { maskContacts } from "./masquage";
import { subjectInTitle } from "../components/nouveauDesign/recherche/searchQuery";
import { PROFILE_PATH, TEACHER_PAGE, teacherPagePath } from "./teacherPage";

// 🔎 Référencement : titre, description et adresse de référence (balise canonical) de chaque page
// publique, posés à chaque changement de page. Google lit ces balises une fois la page affichée.
// Avant : le même titre, la même description et la même canonical (l'accueil) pour toutes les
// pages, le site n'ayant qu'un index.html. Pages privées ou inconnues : titre du site, pas de
// canonical, noindex. La fiche d'un prof pose les siennes (pages/searchTeacher/TeacherDetails.js).
// Jamais touchée : la balise google-site-verification de public/index.html (Search Console).
export const SITE_URL = "https://acadeclic.fr";
// 🔬 Acadeclic, cours de sciences (maths, physique-chimie, SVT, informatique), du collège au
// supérieur, avec la priorité au collège et au lycée. Les parents choisissent souvent. Titres de
// 60 caractères au plus et descriptions de 155 au plus : Google les coupe à la largeur de l'écran
export const SITE_TITLE = "Acadeclic | Cours de maths, physique-chimie et SVT en ligne";

export const PAGES = {
  "/": {
    title: SITE_TITLE,
    description: "Un prof de sciences en ligne en quelques minutes : maths, physique-chimie, SVT, informatique. Du collège au supérieur, profs vérifiés et paiement sécurisé.",
  },
  "/searchTeacher": {
    title: "Trouver un prof de maths, physique-chimie ou SVT | Acadeclic",
    description: "Pour votre enfant ou pour vous, comparez les profs de maths, physique-chimie, SVT et informatique du collège au supérieur, leurs tarifs et leurs avis.",
  },
  "/becomeTeacher": {
    title: "Donner des cours de sciences en ligne | Acadeclic",
    description: "Vous enseignez les maths, la physique-chimie, les SVT ou l'informatique\u00a0? Donnez des cours en ligne du collège au supérieur, une fois votre profil vérifié.",
  },
  "/onlineCourses": {
    title: "Cours de sciences en ligne, tout de suite | Acadeclic",
    description: "Votre enfant bloque sur un exercice de maths, de physique-chimie ou de SVT\u00a0? Choisissez un prof et la durée du cours, puis lancez-le en quelques minutes.",
  },
  "/whoAreWe": {
    title: "Qui sommes-nous | Acadeclic",
    description: "Acadeclic, un cours de sciences au moment où vous en avez besoin. Découvrez l'équipe et ses engagements.",
  },
  "/MyLevel": {
    title: "Les niveaux des professeurs | Acadeclic",
    description: "Les étapes pour progresser comme professeur sur Acadeclic et ce que demande chaque niveau.",
  },
  "/helpcenter": {
    title: "Centre d'aide | Acadeclic",
    description: "Les réponses aux questions fréquentes des élèves et des professeurs d'Acadeclic.",
  },
  "/contact": {
    title: "Contact | Acadeclic",
    description: "Écrivez à l'équipe d'Acadeclic pour toute question sur les cours, les paiements ou votre compte.",
  },
  "/termsCondition": {
    title: "Conditions générales | Acadeclic",
    description: "Les conditions générales d'Acadeclic pour les élèves et les professeurs.",
  },
  "/privacyPolicy": {
    title: "Politique de confidentialité | Acadeclic",
    description: "Comment Acadeclic collecte, utilise et protège vos données personnelles.",
  },
  "/legalNotice": {
    title: "Mentions légales | Acadeclic",
    description: "Les mentions légales d'Acadeclic, éditeur et hébergeur du site.",
  },
};

// Balise <meta name="…"> ou <link rel="…"> de l'en-tête : créée, modifiée, ou retirée (valeur vide)
const setHeadTag = (selector, create, attribute, value) => {
  let tag = document.head.querySelector(selector);
  if (!value) {
    if (tag) tag.remove();
    return;
  }
  if (!tag) {
    tag = create();
    document.head.appendChild(tag);
  }
  tag.setAttribute(attribute, value);
};

const metaNamed = (name) => () => Object.assign(document.createElement("meta"), { name });
const metaProperty = (property) => () => {
  const meta = document.createElement("meta");
  meta.setAttribute("property", property);
  return meta;
};

// Titre, description, adresse de référence (chemin, requête comprise) et consigne aux moteurs
// (robots : « noindex » ou rien). 🔗 Aperçus de partage (tour 55) : mêmes titre, description et
// adresse dans les balises Open Graph et Twitter (image : celle du site, dans index.html). Les pages
// préparées à la construction les ont dans leur HTML (scripts/prerendu.js) : c'est lui que lisent
// WhatsApp, LinkedIn ou Facebook, qui ne lancent pas le programme.
export const applyPageMeta = ({ title = SITE_TITLE, description, path, robots } = {}) => {
  document.title = title;
  if (description) setHeadTag('meta[name="description"]', metaNamed("description"), "content", description);
  setHeadTag('link[rel="canonical"]', () => Object.assign(document.createElement("link"), { rel: "canonical" }), "href", path ? SITE_URL + path : "");
  setHeadTag('meta[name="robots"]', metaNamed("robots"), "content", robots || "");
  setHeadTag('meta[property="og:title"]', metaProperty("og:title"), "content", title);
  setHeadTag('meta[name="twitter:title"]', metaNamed("twitter:title"), "content", title);
  if (description) {
    setHeadTag('meta[property="og:description"]', metaProperty("og:description"), "content", description);
    setHeadTag('meta[name="twitter:description"]', metaNamed("twitter:description"), "content", description);
  }
  setHeadTag('meta[property="og:url"]', metaProperty("og:url"), "content", path ? SITE_URL + path : "");
};

// Recherche d'une matière (?subject=Mathématiques) : titre et adresse de cette matière
const searchMeta = (search) => {
  const subject = new URLSearchParams(search).get("subject");
  if (!subject) return { ...PAGES["/searchTeacher"], path: "/searchTeacher" };
  return {
    title: `Profs de ${subject} en ligne, tout de suite | Acadeclic`,
    description: `Pour votre enfant ou pour vous, comparez les profs de ${subject} en ligne du collège au supérieur, leurs tarifs et les avis des élèves.`,
    path: `/searchTeacher?subject=${encodeURIComponent(subject)}`,
  };
};

// Fiche d'un prof (annonce lue par TeacherDetails.js) : son titre, sa description (sa présentation,
// coordonnées masquées, coupée proprement à 160 caractères), son adresse. noindex si le prof n'est pas
// réservable (annonce en pause, compte bloqué, compte Stripe pas prêt…), d'après l'API (bookable).
// Tour 59 : exactement les balises de la fiche servie par l'API (acadeclic-api/app/helper/FicheProf.js,
// comparées par src/helper/ficheServeur.test.js). Le site ne les change donc pas au chargement
const DESCRIPTION_MAX = 160;
const cutText = (text, max = DESCRIPTION_MAX) => {
  if (text.length <= max) return text;
  const room = text.slice(0, max - 1);
  const end = room.lastIndexOf(" ");
  return `${(end > max / 2 ? room.slice(0, end) : room).replace(/[\s,;:.!?«(–—-]+$/u, "")}…`;
};
export const teacherPageMeta = (advert) => {
  const raw = String(advert?.user_id?.first_name || "").trim();
  const name = raw.charAt(0).toUpperCase() + raw.slice(1);
  const subject = advert?.subject_expertise?.subject_name_fr || advert?.subject_expertise?.subject_name || "";
  const presentation = maskContacts(advert?.advert_description || advert?.user_id?.other_details?.[0]?.about_me || "")
    .replace(/\s+/g, " ")
    .trim();
  return {
    title: subject ? `${name}, prof ${subjectInTitle(subject)} en ligne | Acadeclic` : `${name}, prof particulier en ligne | Acadeclic`,
    description: presentation
      ? cutText(presentation)
      : `Cours particuliers${subject ? ` ${subjectInTitle(subject)}` : ""} en ligne avec ${name}. Présentation, avis des élèves et disponibilités.`,
    // 🔗 Adresse lisible (tour 60, A3)
    path: teacherPagePath({ _id: advert?._id, page_path: advert?.page_path, first_name: advert?.user_id?.first_name, subject }),
    robots: advert?.bookable === true ? undefined : "noindex",
  };
};

// Balises de la page affichée (routes/index.js)
export function PageMeta() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (pathname === "/searchTeacher") return applyPageMeta(searchMeta(search));
    // Fiche d'un prof : la page pose ses balises une fois l'annonce lue (noindex en attendant)
    if (pathname === TEACHER_PAGE || pathname.startsWith(PROFILE_PATH)) return applyPageMeta({ robots: "noindex" });
    const page = PAGES[pathname];
    applyPageMeta(page ? { ...page, path: pathname } : { description: PAGES["/"].description, robots: "noindex" });
  }, [pathname, search]);
  return null;
}
